import { useState, useEffect } from 'react';
import Checkbox from './Checkbox';
import { Label } from './Label';
import { SelectBox } from './SelectBox';
import { Button } from './Button';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useNotification } from 'src/contexts/NotificationContext';

const emailDigestFrequencies = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

interface EmailDigestSettingsProps {
  disabled?: boolean;
}

export const EmailDigestSettings = ({
  disabled = false,
}: EmailDigestSettingsProps) => {
  const { get, patch } = useAuthedRequest();
  const notification = useNotification();

  const [loading, setLoading] = useState(false);
  const [emailDigestEnabled, setEmailDigestEnabled] = useState(false);
  const [emailDigestFrequency, setEmailDigestFrequency] = useState('weekly');

  useEffect(() => {
    const fetchUser = async () => {
      const user = await get(`/users/me`);
      setEmailDigestEnabled(user.emailDigestEnabled);
      setEmailDigestFrequency(
        user.emailDigestFrequency ? user.emailDigestFrequency : 'weekly',
      );
    };
    fetchUser();
  }, []);

  const handleUpdateEmailSettings = async () => {
    setLoading(true);
    try {
      await patch(`/users/me`, {
        emailDigestEnabled,
        emailDigestFrequency: emailDigestEnabled ? emailDigestFrequency : null,
      });
      notification.success('Email settings updated successfully');
    } catch (err) {
      notification.error('Failed to update email settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center">
        <Label htmlFor="email-digest" className="mt-2">
          Email Digest
        </Label>

        <div className="flex items-center gap-2">
          <Checkbox
            ariaLabel="Enable Email Digest"
            checked={emailDigestEnabled}
            onChange={(newChecked) => setEmailDigestEnabled(newChecked)}
            disabled={disabled || loading}
          />
        </div>
      </div>

      {emailDigestEnabled && (
        <div className="mt-4">
          <Label htmlFor="email-frequency">Frequency</Label>
          <SelectBox
            options={emailDigestFrequencies}
            defaultValue={{
              value: emailDigestFrequency || 'daily',
              label: emailDigestFrequency
                ? emailDigestFrequency.charAt(0).toUpperCase() +
                  emailDigestFrequency.slice(1)
                : 'Daily',
            }}
            variant="outlined"
            color="primary"
            size="medium"
            onChange={(value) => setEmailDigestFrequency(value as string)}
            className="w-full"
            disabled={disabled || loading}
          />
        </div>
      )}

      <div className="mt-4">
        <Button
          className="w-full"
          onClick={handleUpdateEmailSettings}
          disabled={disabled || loading}
          variant="contained"
          color="primary"
          size="medium"
        >
          {loading ? 'Saving...' : 'Update Email Settings'}
        </Button>
      </div>
    </div>
  );
};
